import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import EmptyState from "./EmptyState.jsx";

/**
 * This is what a table does.
 *
 * Every table in this console has been a bug at 390px at least once. The
 * Connectors concepts table pushed a 375px viewport to 607 and took the whole
 * page sideways with it; the fix was a `<Box sx={{ overflowX: "auto" }}>`
 * around it, applied by hand, at one call site, by somebody who had just been
 * bitten. The next table was written by somebody who had not been.
 *
 * So the container is not a prop and cannot be turned off. `TableContainer`
 * with `overflowX: "auto"` is the component, and a caller who wants a table
 * gets the scroll behaviour whether or not they were thinking about phones.
 * The body never scrolls sideways because a table cannot make it.
 *
 * ## Columns are data
 *
 * `columns` is a list, not JSX, because the columns on the screens that
 * matter come from `GET /schema` and a component that took children would
 * push every caller back into writing them out. The shape is deliberately
 * close to what the kernel already publishes per field:
 *
 * ```js
 * columns={[
 *   { key: "id", label: "Kernel id", mono: true },
 *   ...type.fields.map((f) => ({
 *     key: f.name,
 *     label: f.name,
 *     align: f.minor_units ? "right" : "left",
 *     render: (row) => cell(row, f),
 *   })),
 * ]}
 * ```
 *
 * A type added to the kernel appears with no change to the table.
 *
 * ## `tabular-nums`, and where
 *
 * On anything right-aligned, automatically. Right-alignment on a column is
 * how you say "these are quantities and you will compare them", and
 * proportional digits make a column of quantities ragged in the one place
 * raggedness is misread as a different magnitude. `mono` does the same for
 * ids and hashes, which are compared character by character rather than read.
 *
 * Deriving it from `align` rather than asking for it means the two cannot
 * disagree, which they did: `Objects` had `tabular-nums` on its money columns
 * and `Access` had right-aligned counts without it, in the same console, one
 * screen apart.
 *
 * ## Empty is not a header over nothing
 *
 * A table that renders its head and no body claims a query ran and found the
 * columns interesting. It renders `EmptyState` instead, which says which of
 * the three empties this is — and a caller who wants their own sentence
 * passes `empty`.
 */
export default function DataTable({
  columns,
  rows,
  getRowKey,
  onRowClick,
  empty,
  emptyTitle = "No rows",
  tagged,
  caption,
  size = "small",
  stickyHeader = false,
  sx,
  ...rest
}) {
  const list = rows || [];

  if (!list.length) {
    return empty !== undefined ? empty : (
      <EmptyState title={emptyTitle} tagged={tagged} sx={sx} />
    );
  }

  const keyFor = getRowKey || ((row, index) => row?.id ?? index);

  return (
    <>
      <TableContainer
        component={Paper}
        variant="outlined"
        // Not a prop. See above: this is the whole reason the component
        // exists, and a caller able to switch it off is a caller who will.
        sx={{ overflowX: "auto", maxWidth: "100%", ...sx }}
      >
        <Table size={size} stickyHeader={stickyHeader} {...rest}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  sx={column.width ? { width: column.width } : null}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row, index) => (
              <TableRow
                key={keyFor(row, index)}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={onRowClick ? { cursor: "pointer" } : null}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || "left"}
                    sx={cellSx(column)}
                  >
                    {column.render ? column.render(row, index) : row?.[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {caption ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {caption}
        </Typography>
      ) : null}
    </>
  );
}

function cellSx(column) {
  const sx = { ...(column.sx || {}) };
  if (column.mono) {
    sx.fontFamily = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
    sx.fontSize = "0.75rem";
  }
  // Right-aligned means "compare these", and comparing is what proportional
  // digits break.
  if (column.align === "right" || column.numeric || column.mono) {
    sx.fontVariantNumeric = "tabular-nums";
  }
  if (column.width) sx.width = column.width;
  return sx;
}
